import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Grid, IconButton, makeStyles, Paper, Chip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import EventIcon from '@material-ui/icons/Event';

// 樣式定義
const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        padding: theme.spacing(2),
        backgroundColor: "#f7f7f7", 
        borderBottom: '1px solid #ccc',
    },
    customTitleContent: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: "bold",
        width: '100%',
    },
    dialogContent: {
        padding: theme.spacing(3),
    },
    rowLabel: {
        backgroundColor: "#f7f7f7", 
        fontWeight: "bold",
        padding: theme.spacing(1.5),
        borderRight: "1px solid #eee",
        minWidth: 120, 
        textAlign: "left",
        display: 'flex',
        alignItems: 'center',
    },
    rowContent: {
        padding: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    tableGrid: {
        border: "1px solid #ccc",
    },
    emailChip: {
        margin: theme.spacing(0.5),
        borderColor: "#5597b4",
        color: "#5597b4",
        backgroundColor: "transparent",
        "& svg": {
            color: "#5597b4",
        },
    },
    addButton: {
        backgroundColor: "#5597b4",
        color: "#fff",
        marginLeft: theme.spacing(1),
        "&:hover": {
            backgroundColor: "#3E8CCB",
        },
    },
    saveButton: {
        backgroundColor: "#5597b4",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#3E8CCB",
        },
    },
    cancelButton: {
        color: "#999",
        border: "1px solid #ccc",
        "&:hover": {
            backgroundColor: "#eee",
        },
    },
}));

// Email 格式驗證函式
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

function EventNotificationEditDialog({ open, onClose, eventId, onSave, getEventNotification }) {
    const classes = useStyles();
    const [eventData, setEventData] = useState({
        windFarmName: "海能",
        event_name: "",
        event_content: "",
        note: "",
        notification_date: "",
        notification_time: "",
        deadline_date: "",
        emails: [],
        windcontract_id: "",
        event_notification_id: "",
    });
    const [newRecipient, setNewRecipient] = useState("");
    const [emailError, setEmailError] = useState("");
    const [loading, setLoading] = useState(true);

    // 獲取事件資料
    useEffect(() => {
        if (open && eventId) {
            setLoading(true);
            getEventNotification(eventId)
                .then((response) => {
                    console.log(response);
                    const data = response.data;
                    setEventData({
                        windFarmName: data.windcontract_name || "",
                        event_name: data.event_name || "",
                        event_content: data.event_content || "",
                        note: data.note || "",
                        notification_date: data.notification_date.replace('T', ' ').split(' ')[0] || "",
                        notification_time: data.notification_time || "",
                        deadline_date: data.deadline_date || "",
                        emails: data.emails || [],
                        windcontract_id: data.windcontract_id || "",
                        event_notification_id: data.event_notification_id || "",
                    });
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching event:", error);
                    setLoading(false);
                });
        }
    }, [open, eventId, getEventNotification]);

    // 處理新增收件者
    const handleAddRecipient = () => {
        if (!newRecipient) return;
        
        const trimmedEmail = newRecipient.trim();

        if (!isValidEmail(trimmedEmail)) {
            setEmailError("Email 格式錯誤");
            return;
        }

        if (eventData.emails.includes(trimmedEmail)) {
            setEmailError("此 Email 已存在");
            return;
        }

        setEventData({
            ...eventData,
            emails: [...eventData.emails, trimmedEmail],
        });
        setNewRecipient("");
        setEmailError("");
    };

    // 處理刪除收件者
    const handleDeleteRecipient = (emailToDelete) => {
        setEventData({
            ...eventData,
            emails: eventData.emails.filter(email => email !== emailToDelete),
        });
    };

    // 處理儲存
    const handleSaveClick = () => {
        const payload = {
            windcontract_id: eventData.windcontract_id,
            event_notification_id: eventId,
            event_name: eventData.event_name,
            event_content: eventData.event_content,
            note: eventData.note,
            notification_date: eventData.notification_date,
            notification_time: eventData.notification_time,
            deadline_date: eventData.deadline_date,
            emails: eventData.emails,
        };

        if (!eventData.event_name || !eventData.event_content || !eventData.notification_date || !eventData.notification_time || !eventData.deadline_date) {
            alert("請填寫所有必填欄位 (事件標題、事件內容、通知日期、通知時間、截止日期)");
            return;
        }
        
        console.log("Saving payload:", payload);
        
        onSave({ 
            data: payload,
            status: 1, 
            sheetId: eventId,
            type: "update"
        });
        
        onClose();
    };

    const CustomDialogTitle = (props) => {
        const { children, onClose } = props;
        return (
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <div className={classes.customTitleContent}>
                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>{children}</Typography>
                    {onClose ? (
                        <IconButton aria-label="close" onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    ) : null}
                </div>
            </DialogTitle>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md"
            fullWidth
            aria-labelledby="event-notification-edit-dialog-title"
        >
            <CustomDialogTitle id="event-notification-edit-dialog-title" onClose={onClose}>
                編輯事件提醒
            </CustomDialogTitle>
            
            <DialogContent dividers className={classes.dialogContent}>
                {loading ? (
                    <Typography>載入中...</Typography>
                ) : (
                    <Paper variant="outlined" className={classes.tableGrid}>
                        {/* 風場名稱 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                風場名稱
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <Typography>{eventData.windFarmName}</Typography>
                            </Grid>
                        </Grid>

                        {/* 事件標題 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                事件標題
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={eventData.event_name}
                                    onChange={(e) => setEventData({ ...eventData, event_name: e.target.value })}
                                    placeholder="請輸入事件標題"
                                />
                            </Grid>
                        </Grid>

                        {/* 事件內容 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                事件內容
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={eventData.event_content}
                                    onChange={(e) => setEventData({ ...eventData, event_content: e.target.value })}
                                    placeholder="請輸入事件內容"
                                />
                            </Grid>
                        </Grid>

                        {/* 備註 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                備註
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={eventData.note}
                                    onChange={(e) => setEventData({ ...eventData, note: e.target.value })}
                                    placeholder="請輸入備註"
                                />
                            </Grid>
                        </Grid>

                        {/* 通知日期 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                通知日期
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    type="date"
                                    value={eventData.notification_date}
                                    onChange={(e) => setEventData({ ...eventData, notification_date: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        {/* 通知時間 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                通知時間
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    type="time"
                                    value={eventData.notification_time}
                                    onChange={(e) => setEventData({ ...eventData, notification_time: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        {/* 截止日期 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                截止日期
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    type="datetime-local"
                                    value={eventData.deadline_date}
                                    onChange={(e) => setEventData({ ...eventData, deadline_date: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        {/* 新增外部收件者 */}
                        <Grid container style={{ borderBottom: '1px solid #eee' }}>
                            <Grid item xs={3} className={classes.rowLabel}>
                                新增外部收件者
                            </Grid>
                            <Grid item xs={9} className={classes.rowContent}>
                                <Box display="flex" alignItems="flex-start" flexDirection="column">
                                    <Box display="flex" alignItems="center" width="100%">
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            placeholder="請輸入收件者電子信箱"
                                            value={newRecipient}
                                            onChange={(e) => {
                                                setNewRecipient(e.target.value);
                                                setEmailError("");
                                            }}
                                            error={!!emailError}
                                            helperText={emailError}
                                        />
                                        <Button 
                                            variant="contained" 
                                            className={classes.addButton} 
                                            onClick={handleAddRecipient}
                                        >
                                            新增
                                        </Button>
                                    </Box>
                                    
                                    {/* 收件者清單 */}
                                    <Box mt={1} width="100%">
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                            收件者清單:
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap">
                                            {eventData.emails.map((email) => (
                                                <Chip
                                                    key={email}
                                                    label={email}
                                                    onDelete={() => handleDeleteRecipient(email)}
                                                    variant="outlined" 
                                                    className={classes.emailChip}
                                                    deleteIcon={<CloseIcon />}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px' }}>
                <Button 
                    onClick={onClose} 
                    className={classes.cancelButton}
                    variant="outlined"
                >
                    取消
                </Button>
                <Button 
                    onClick={handleSaveClick} 
                    className={classes.saveButton}
                    variant="contained"
                >
                    儲存
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EventNotificationEditDialog;